import { makeStyles } from '@material-ui/core/styles';
import * as d3 from 'd3';
import moment from 'moment';
import Moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';
import { fromEvent, timer } from 'rxjs';
import { debounce, map } from 'rxjs/operators';

import { FeedPostDetailsFragment, PostType } from '../../../graphql/gen/types';
import { colorPalette } from '../../../theme';
import { CustomTooltip, rectangleWidth, ShapeCollapsed, ShapeExpanded } from './TimelineGraphBox';
import { getPostHeights } from './utils';

type Props = {
  posts: FeedPostDetailsFragment[];
  start: Moment.Moment;
  finish: Moment.Moment;
};

const HEIGHT_PER_RECTANGLE_EXPANDED = 10;
const ONE_DAY = 60 * 60 * 24 * 1000;

type ScatterItem = {
  title: string;
  x: number;
  y: number;
  date: string;
  id: string;
  type: PostType;
  expanded: boolean;
};

const getPostDate = (post: FeedPostDetailsFragment): string => post.eventDate;

const getExpandedScatterData = (
  posts: Array<FeedPostDetailsFragment & { dayStart: number }>,
  rectangleWidth: number
): { maxHeight: number; expandedData: ScatterItem[] } => {
  // we're displaying all the posts/events published during ticksPerRectangle days one under the other in the sameBucket.

  const postHeights = getPostHeights(posts, rectangleWidth);
  const maxHeight = postHeights.length > 0 ? Math.max(...postHeights.map((post) => post.height)) : 0;
  const expandedData = postHeights
    .map((p) => ({
      title: p.title,
      x: p.dayStart,
      y: p.height,
      date: getPostDate(p),
      id: p.id,
      type: p.type,
      expanded: true,
      color: p.plex.color,
    }))
    // recharts counts height from bottom to top even if the xAxis is on the top. But we want to display the data from
    // top to bottom,
    .map((item) => ({ ...item, y: (maxHeight - item.y + 1) * HEIGHT_PER_RECTANGLE_EXPANDED }));
  return { expandedData, maxHeight };
};

const getCollapsedScatterData = (
  posts: Array<FeedPostDetailsFragment & { dayStart: number }>,
  maxHeight: number
): ScatterItem[] => {
  let currentHeight = 0,
    lastItemBucketStart = 0;
  return posts.map((post) => {
    if (post.dayStart > lastItemBucketStart) {
      currentHeight = 1;
    } else {
      currentHeight++;
    }
    lastItemBucketStart = post.dayStart;
    return {
      title: post.title,
      x: post.dayStart,
      y: HEIGHT_PER_RECTANGLE_EXPANDED * (maxHeight + 1) - currentHeight - 2,
      date: getPostDate(post),
      id: post.id,
      type: post.type,
      expanded: false,
      color: post.plex.color,
    };
  });
};

const REMAINING_WIDTH = 700;

export const TimelineGraph = (props: Props): React.ReactElement | null => {
  const { posts, start, finish } = props;
  const classes = useStyles();
  const startDate = d3.timeDay.floor(start.toDate());
  const finishDate = d3.timeDay.floor(finish.toDate());
  const days = Math.round(moment.duration(finish.diff(start)).asDays());
  const sortedPosts = posts
    .map((post) => ({
      ...post,
      dayStart: moment(getPostDate(post)).startOf('day').unix() * 1000,
    }))
    .sort((a, b) => a.dayStart - b.dayStart);
  const [timelineWidth, setTimelineWidth] = useState(window.innerWidth - REMAINING_WIDTH); // left navbar and plex details take 700px

  useEffect(() => {
    const observable = fromEvent(window, 'resize').pipe(
      debounce(() => timer(500)),
      map(() => {
        setTimelineWidth(window.innerWidth - REMAINING_WIDTH);
      })
    );
    const sub = observable.subscribe();

    // Cleanup event listener
    return (): void => {
      sub.unsubscribe();
    };
  }, []);

  const rectangleCountInWidth = timelineWidth / (rectangleWidth + 10); // 10px padding
  const rectangleWidthInDays = Math.ceil(days / rectangleCountInWidth) * ONE_DAY;

  const { maxHeight, expandedData } = getExpandedScatterData(sortedPosts, rectangleWidthInDays);
  const collapsedData = getCollapsedScatterData(sortedPosts, maxHeight);

  const timeInterval = d3.timeDay.every(1);
  if (!timeInterval) {
    // this shouldn't happen, every returns TimeInterval | null
    return null;
  }

  const ticks = d3
    .scaleTime()
    .domain([startDate, finishDate])
    .ticks(timeInterval)
    .map((date) => date.getTime());
  const height = maxHeight * 50 + 125; // 50px for each expanded box, and 125 for the collapsed line
  return (
    <div className={classes.graphHeader} style={{ height }}>
      <div className={classes.whiteBackground} />
      {/* hack for recharts responsiveContainer see https://github.com/all-hands-hq/source/pull/1212*/}
      <div className={classes.positionAbsolute}>
        <ResponsiveContainer width="100%" height={height}>
          <ScatterChart
            margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: -30,
            }}
          >
            <XAxis
              dataKey="x"
              domain={[startDate.getTime(), finishDate.getTime()]}
              orientation="top"
              padding={{ left: 10, right: 100 }}
              scale="time"
              type="number"
              ticks={ticks}
              tickFormatter={(tick: number): string => d3.timeFormat('%d')(new Date(tick))}
            />
            <YAxis
              type="number"
              dataKey="y"
              tick={false}
              domain={[0, (maxHeight + 1) * HEIGHT_PER_RECTANGLE_EXPANDED]}
              axisLine={false}
            />
            <Scatter name="Expanded" data={expandedData} shape={ShapeExpanded} />
            <Scatter name="Collapsed" data={collapsedData} stroke="#fff" shape={ShapeCollapsed} />
            <Tooltip content={CustomTooltip} cursor={false} />
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  graphHeader: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '7px 0',
    backgroundColor: colorPalette.purpleGray._50,
    position: 'relative',
  },
  positionAbsolute: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
  },
  whiteBackground: {
    backgroundColor: '#fff',
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: 90,
  },
  th: {
    display: 'flex',
    alignItems: 'flex-start',
    flex: 1,
    paddingLeft: 40,
  },
  body: {
    backgroundColor: colorPalette.purpleGray._50,
    display: 'flex',
    flexDirection: 'row',
    minHeight: 150,
  },
}));
